/* eslint-disable @bigbinary/neeto/use-common-routes */
import { toLabelAndValue, isPresent, noop } from "neetocist";
import PrivateRoute from "neetocommons/react-utils/PrivateRoute";
import PropTypes from "prop-types";
import { Route, Switch, useRouteMatch } from "react-router-dom";

import ErrorPage from "components/ErrorPage";

import MainPanel from "./MainPanel";
import QRCode from "./QRCode";
import "./share-via-link.scss";
import SocialMedia from "./SocialMedia";

const ShareViaLink = ({
  title,
  description,
  isLoading = false,
  enabledOptions = { regenerate: false, edit: false },
  urls = [],
  entity,
  entityName,
  handleEdit = noop,
  handleRegenerate = noop,
  disableShareSocialMedia = false,
  disableQRDownload = false,
  socialMediaProps,
  qrCodeProps,
  moreOptions,
  publicLinkExpiryConfiguration = {},
  customTopContent,
  customBottomContent,
}) => {
  const { path: sharePath, url: shareUrl } = useRouteMatch();

  return (
    <Switch>
      <Route
        exact
        path={sharePath}
        render={() => (
          <MainPanel
            {...{
              customBottomContent,
              customTopContent,
              description,
              disableQRDownload,
              disableShareSocialMedia,
              enabledOptions,
              entity,
              entityName,
              handleEdit,
              handleRegenerate,
              isLoading,
              moreOptions,
              publicLinkExpiryConfiguration,
              qrCodeProps,
              socialMediaProps,
              title,
              urls,
            }}
          />
        )}
      />
      <Route
        exact
        path={`${sharePath}/qrcode`}
        render={() => (
          <QRCode {...{ entity, qrCodeProps, shareUrl }} url={urls[0]?.url} />
        )}
      />
      <Route
        exact
        path={`${sharePath}/social-media`}
        render={() => (
          <SocialMedia {...{ shareUrl, socialMediaProps }} url={urls[0]?.url} />
        )}
      />
      {isPresent(moreOptions) &&
        moreOptions.map(({ link, element, path, permissions }) => {
          if (!link && !path) return null;

          return (
            <PrivateRoute
              {...{ permissions }}
              exact
              component={() => element}
              key={link}
              path={path || link}
            />
          );
        })}
      <Route component={ErrorPage} path="*" />
    </Switch>
  );
};

ShareViaLink.propTypes = {
  /**
   * The title of the share via link component.
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * The description of the share via link component.
   */
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * The custom content to be shown in the top of the share panel.
   */
  customTopContent: PropTypes.element,
  /**
   * The custom content to be shown in the bottom of the share panel.
   */
  customBottomContent: PropTypes.element,
  /**
   * The boolean value to check if the link is being regenerated or updated.
   */
  isLoading: PropTypes.bool,
  /**
   * An object which accepts two boolean values that decide if the dropdown options are hidden or not. If both are false, then the dropdown is hidden as well.
   */
  enabledOptions: PropTypes.shape({
    regenerate:
      PropTypes.bool ||
      PropTypes.shape({
        expiresInOptions: PropTypes.arrayOf(toLabelAndValue(PropTypes.string)),
      }),
    edit: PropTypes.bool,
  }),
  /**
   * The entity object for which the link is being shared.
   */
  entity: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  /**
   * The type of the entity for which the link is being shared.
   */
  entityName: PropTypes.string,
  /**
   * The function that is called when the Edit URL form is submitted. It returns the updated URL value.
   */
  handleEdit: PropTypes.func,
  /**
   * The function which makes the API call to regenerate the url.
   */
  handleRegenerate: PropTypes.func,
  /**
   * To customize the social media card and its dedicated page.
   */
  socialMediaProps: PropTypes.shape({
    cardTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    cardDescription: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    pageTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    pageDescription: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    postTitle: PropTypes.string,
  }),
  /**
   * To customize the QR code card and its dedicated page.
   */
  qrCodeProps: PropTypes.shape({
    cardTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    cardDescription: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    pageTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    pageDescription: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  }),
  /**
   * The props that are passed to the edit URL field. `prefix` and `editablePath` decide
   * which part of the URL is to be added in the prefix and which part is editable. Default values are taken from `url`.
   * `maxLength` decides if the entered URL should be limited to specified number of characters.
   * `previewUrl` will be used for previewing the entity (optional).
   */
  urls: PropTypes.arrayOf(
    PropTypes.shape({
      prefix: PropTypes.string,
      editablePath: PropTypes.string,
      maxLength: PropTypes.number,
      url: PropTypes.string,
      previewUrl: PropTypes.string,
    })
  ),
  /**
   * To share using alternative options
   */
  moreOptions: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      link: PropTypes.string,
      path: PropTypes.string,
      icon: PropTypes.elementType,
      onClick: PropTypes.func,
      dataTestid: PropTypes.string,
      element: PropTypes.element,
      subtitleProps: PropTypes.shape({
        subtitle: PropTypes.string,
        dataTestid: PropTypes.string,
        link: PropTypes.string,
        onClick: PropTypes.func,
      }),
    })
  ),
  /**
   * To disable share social media section.
   */
  disableShareSocialMedia: PropTypes.bool,
  /**
   * To disable QR code download section.
   */
  disableQRDownload: PropTypes.bool,
};

export default ShareViaLink;
